import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const auth = token => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} });

/**
 * The business date as the server writes it.
 *
 * `orders.order_date` is a DATE filled from the server's *local* `date.today()`, so
 * the client's day must be built from local parts too. `toISOString().slice(0,10)`
 * re-serialises to UTC and hands back yesterday for every bill settled between
 * midnight and 05:30 IST — the exact window a manager closes the night in.
 */
export const businessDay = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * Settled bills for one business date.
 *
 * `/api/admin/orders` rather than `/api/orders?status=closed`: the latter is the
 * live board's query — it orders by created_at ASCENDING and caps at 200, so on an
 * outlet with any history it returns the first 200 bills ever closed rather than
 * today's, and its `amount` is the gross of the order lines, not the settled total
 * (no discount, no GST, no round-off). This route filters on order_date, sorts
 * newest first, and returns `total` and `payment_method` — the figures that were
 * actually collected. Both routes sit behind the same `pages.order_history`
 * permission, so this is not a wider door.
 *
 * Returns `{ orders, total }` where `total` is the row count, not money.
 */
export const listClosedOrders = ({ day, limit = 100 } = {}, token) =>
  axios
    .get(`${API}/admin/orders`, {
      ...auth(token),
      params: { status: "closed", date_from: day, date_to: day, limit },
    })
    .then(r => r.data);

/**
 * Correct how a settled bill was paid (owner, 2026-10-24). Only the tenders
 * change; they must add up to the bill. `version` guards against a bill that
 * changed under the cashier.
 */
export const changePayment = (orderId, version, payments, token) =>
  axios
    .post(
      `${API}/orders/${orderId}/change-payment`,
      { payments },
      { headers: { ...auth(token).headers, "If-Match": String(version) } },
    )
    .then(r => r.data);

/** One settled bill with its lines and the money breakdown that was charged. */
export const getClosedOrder = (orderId, token) =>
  axios.get(`${API}/admin/orders/${orderId}`, auth(token)).then(r => r.data);

/**
 * Re-queue the bill for a settled order.
 *
 * POST /api/admin/orders/{id}/reprint with `type: "bill"` — the same route the
 * KOT reprint uses. The service re-runs PrintService.create_bill_job with a fresh
 * idempotency nonce (so it does not collide with the original job), writes a
 * `bill.reprinted` audit row, and answers 201 with `{ job_ids: [...] }` — one id
 * per printer/copy. `restaurant_settings.post_bill_reprint_required` does NOT
 * gate this: migration 024 creates the row but nothing in backend/app reads it
 * (see the audit note in lib/settingsMap.js). The only gate is the
 * `orders.reprint` permission, enforced server-side.
 *
 * Returns the raw payload; the caller counts `job_ids` rather than assuming one.
 */
export const reprintBill = (orderId, token) =>
  axios
    .post(`${API}/admin/orders/${orderId}/reprint`, { type: "bill" }, auth(token))
    .then(r => r.data);

/** Re-queue one KOT of an order at its stations. Same route, `type: "kot"`. */
export const reprintKot = (orderId, kotNumber, token) =>
  axios
    .post(`${API}/admin/orders/${orderId}/reprint`, { type: "kot", kot_number: kotNumber }, auth(token))
    .then(r => r.data);

/* ── Orders/KOT History page ──────────────────────────────────────────────
 *
 * Two ledgers behind one filter panel. Both gated on `pages.order_history`.
 * Each answers `{ rows…, total, abilities: { reprint, override } }` — the
 * abilities are the caller's effective permissions, so the page draws only the
 * controls this session can actually use (§45). The server re-checks on every
 * action regardless.
 */

/** Bills: every order that reached billing, settlement, or a post-KOT void. */
export const listBillHistory = (params, token) =>
  axios.get(`${API}/orders-history/orders`, { ...auth(token), params }).then(r => r.data);

/** KOTs: one row per punched KOT across every order status. */
export const listKotHistory = (params, token) =>
  axios.get(`${API}/orders-history/kots`, { ...auth(token), params }).then(r => r.data);

/** The outlet's payment modes — the payment-type filter is built from these,
 * never from a hardcoded list (§46). */
export const listPaymentModes = token =>
  axios.get(`${API}/payment-modes`, auth(token)).then(r => r.data);

/**
 * Admin override: put a finished bill back on the floor so it can be edited.
 * `orders.override`, audited as `order.reopened`. Returns the order.
 */
export const reopenOrder = (orderId, token) =>
  axios.post(`${API}/admin/orders/${orderId}/reopen`, {}, auth(token)).then(r => r.data);

/**
 * Admin override: void a bill outright. `POST /admin/orders/{id}/force-status`
 * with `cancelled` — the bill leaves the floor and the day's sales, its bill
 * number stays burned (§11), and the mandatory `notes` is the only record of
 * why. `orders.override`, enforced server-side.
 */
export const voidOrder = (orderId, notes, token) =>
  axios
    .post(`${API}/admin/orders/${orderId}/force-status`, { status: "cancelled", notes }, auth(token))
    .then(r => r.data);
